import React, { useEffect, useRef, useState } from 'react';
import { useLocation, Routes, Route } from 'react-router-dom';
import { CustomRoutes } from '../../consts/routes/routes';
import { recordPageView, recordSession, updatePageView } from '../../services/analytics-service';

const RouteProvider = () => {
    const location = useLocation();
    const entryTimeRef = useRef(Date.now());
    const currentPathRef = useRef(location.pathname);
    const inactivityTimeRef = useRef(0);
    const inactiveStartRef = useRef(null);
    const [pageViewId, setPageViewId] = useState(null);
    const pageViewIdRef = useRef(null);

    const updatePageViewOnExit = () => {
        if (!pageViewIdRef.current) return;

        const exitTime = Date.now();
        if (inactiveStartRef.current) {
            inactivityTimeRef.current += exitTime - inactiveStartRef.current;
            inactiveStartRef.current = null;
        }

        const updatePayload = {
            exitTime: new Date(exitTime).toISOString(),
            timeSpent: Math.floor((exitTime - entryTimeRef.current) / 1000),
            inactivityDuration: Math.floor(inactivityTimeRef.current / 1000),
            pageViewId: pageViewIdRef.current
        };

        inactivityTimeRef.current = 0;
        
        updatePageView(updatePayload).then(res => {
            inactivityTimeRef.current = 0
        }).catch(res => console.log(res));
    };

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        entryTimeRef.current = Date.now()
        const initializeSession = async () => {
            const existingSessionId = sessionStorage.getItem('session_id');
            const entryTime = Date.now();
            currentPathRef.current = location.pathname;
            let isInitialSession = true;

            if (existingSessionId) {
                isInitialSession = false;
            }

            if (isInitialSession) {
                const ip = await fetch('https://free.freeipapi.com/api/json');
                const ipData = await ip.json();
                
                const payload = {
                    country: ipData.countryName,
                    city: ipData.cityName,
                    userAgent: navigator.userAgent,
                    referrer: document.referrer || null,
                    screenWidth: window.innerWidth,
                    screenHeight: window.innerHeight,
                    language: navigator.language || null,
                    path: location.pathname,
                    entryTime: new Date(entryTime).toISOString(),
                    isInitialSession
                };

                const response = await recordSession(payload);
                
                if (response?.sessionId) {
                    sessionStorage.setItem('session_id', response.sessionId);
                    pageViewIdRef.current = response.pageViewId;
                    console.log('Session initialized:', response.sessionId);
                } else console.warn('Failed to initialize session');
            } else {
                const response = await recordSession({
                    path: location.pathname,
                    entryTime: new Date(entryTime).toISOString(),
                    currentSessionId: existingSessionId,
                    isInitialSession
                });

                if (response?.sessionId) {
                    pageViewIdRef.current = response.pageViewId;
                }
            }
        };

        initializeSession();

        window.addEventListener('beforeunload', updatePageViewOnExit);

        return () => {
            window.removeEventListener('beforeunload', updatePageViewOnExit);
            updatePageViewOnExit();
        };
    }, [location]);

    useEffect(() => {
        const handleInactiveStart = () => {
            if (!inactiveStartRef.current) {
                inactiveStartRef.current = Date.now();
            }
        };

        const handleInactiveEnd = () => {
            if (inactiveStartRef.current) {
                const now = Date.now();
                inactivityTimeRef.current += now - inactiveStartRef.current;
                inactiveStartRef.current = null;
            }
        };

        document.addEventListener('visibilitychange', () => {
            if (document.hidden) handleInactiveStart();
            else handleInactiveEnd();
        });

        window.addEventListener('blur', handleInactiveStart);
        window.addEventListener('focus', handleInactiveEnd);

        return () => {
            handleInactiveEnd();
            document.removeEventListener('visibilitychange', handleInactiveStart);
            window.removeEventListener('blur', handleInactiveStart);
            window.removeEventListener('focus', handleInactiveEnd);
        };
    }, []);

    return (
        <Routes>
            {CustomRoutes.map(route => (
                <Route key={route.path} path={route.path} element={route.element} />
            ))}
        </Routes>
    );
};

export default RouteProvider;